"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.AwaitQueue = exports.AwaitQueueRemovedTaskError = exports.AwaitQueueStoppedError = void 0;
const Logger_1 = require("./Logger");
const logger = new Logger_1.Logger();
/**
 * Custom Error derived class used to reject pending tasks once stop() method
 * has been called.
 */
class AwaitQueueStoppedError extends Error {
  constructor(message) {
    super(message !== null && message !== void 0 ? message : 'AwaitQueue stopped');
    this.name = 'AwaitQueueStoppedError';
    // @ts-ignore
    if (typeof Error.captureStackTrace === 'function') {
      // @ts-ignore
      Error.captureStackTrace(this, AwaitQueueStoppedError);
    }
  }
}
exports.AwaitQueueStoppedError = AwaitQueueStoppedError;
/**
 * Custom Error derived class used to reject pending tasks once removeTask()
 * method has been called.
 */
class AwaitQueueRemovedTaskError extends Error {
  constructor(message) {
    super(message !== null && message !== void 0 ? message : 'AwaitQueue task removed');
    this.name = 'AwaitQueueRemovedTaskError';
    // @ts-ignore
    if (typeof Error.captureStackTrace === 'function') {
      // @ts-ignore
      Error.captureStackTrace(this, AwaitQueueRemovedTaskError);
    }
  }
}
exports.AwaitQueueRemovedTaskError = AwaitQueueRemovedTaskError;
class AwaitQueue {
  constructor() {
    // Queue of pending tasks (map of PendingTasks indexed by id).
    this.pendingTasks = new Map();
    // Incrementing PendingTask id.
    this.nextTaskId = 0;
    // Whether stop() method is stopping all pending tasks.
    this.stopping = false;
  }
  get size() {
    return this.pendingTasks.size;
  }
  async push(task, name) {
    var _name;
    name = (_name = name) !== null && _name !== void 0 ? _name : task.name;
    logger.debug("push() [name:".concat(name, "]"));
    if (typeof task !== 'function') {
      throw new TypeError('given task is not a function');
    }
    if (name) {
      try {
        Object.defineProperty(task, 'name', {
          value: name
        });
      } catch (error) {}
    }
    return new Promise((resolve, reject) => {
      const pendingTask = {
        id: this.nextTaskId++,
        task: task,
        name: name,
        enqueuedAt: Date.now(),
        executedAt: undefined,
        completed: false,
        resolve: result => {
          // pendingTask.resolve() can only be called in execute() method. Since
          // resolve() was called it means that the task successfully completed.
          // However the task may have been stopped before it completed (via
          // stop() or remove()) so its completed flag was already set. If this
          // is the case, abort here since next task (if any) is already being
          // executed.
          if (pendingTask.completed) {
            return;
          }
          pendingTask.completed = true;
          // Remove the task from the queue.
          this.pendingTasks.delete(pendingTask.id);
          logger.debug("resolving task [name:".concat(pendingTask.name, "]"));
          // Resolve the task with the obtained result.
          resolve(result);
          // Execute the next pending task (if any).
          const [nextPendingTask] = this.pendingTasks.values();
          // NOTE: During the resolve() callback the user app may have interacted
          // with the queue. For instance, the app may have pushed a task while
          // the queue was empty so such a task is already being executed. If so,
          // don't execute it twice.
          if (nextPendingTask && !nextPendingTask.executedAt) {
            void this.execute(nextPendingTask);
          }
        },
        reject: error => {
          // pendingTask.reject() can be called within execute() method if the
          // task completed with error. However it may have also been called in
          // stop() or remove() methods (before or while being executed) so its
          // completed flag was already set. If so, abort here since next task
          // (if any) is already being executed.
          if (pendingTask.completed) {
            return;
          }
          pendingTask.completed = true;
          // Remove the task from the queue.
          this.pendingTasks.delete(pendingTask.id);
          logger.debug("rejecting task [name:".concat(pendingTask.name, "]: %s"), String(error));
          // Reject the task with the obtained error.
          reject(error);
          // Execute the next pending task (if any) unless stop() is running.
          if (!this.stopping) {
            const [nextPendingTask] = this.pendingTasks.values();
            // NOTE: During the reject() callback the user app may have interacted
            // with the queue. For instance, the app may have pushed a task while
            // the queue was empty so such a task is already being executed. If so,
            // don't execute it twice.
            if (nextPendingTask && !nextPendingTask.executedAt) {
              void this.execute(nextPendingTask);
            }
          }
        }
      };
      // Append task to the queue.
      this.pendingTasks.set(pendingTask.id, pendingTask);
      // And execute it if this is the only task in the queue.
      if (this.pendingTasks.size === 1) {
        void this.execute(pendingTask);
      }
    });
  }
  stop() {
    logger.debug('stop()');
    this.stopping = true;
    for (const pendingTask of this.pendingTasks.values()) {
      logger.debug("stop() | stopping task [name:".concat(pendingTask.name, "]"));
      pendingTask.reject(new AwaitQueueStoppedError());
    }
    this.stopping = false;
  }
  remove(taskIdx) {
    logger.debug("remove() [taskIdx:".concat(taskIdx, "]"));
    const pendingTask = Array.from(this.pendingTasks.values())[taskIdx];
    if (!pendingTask) {
      logger.debug("stop() | no task with given idx [taskIdx:".concat(taskIdx, "]"));
      return;
    }
    pendingTask.reject(new AwaitQueueRemovedTaskError());
  }
  dump() {
    const now = Date.now();
    let idx = 0;
    return Array.from(this.pendingTasks.values()).map(pendingTask => ({
      idx: idx++,
      task: pendingTask.task,
      name: pendingTask.name,
      enqueuedTime: pendingTask.executedAt ? pendingTask.executedAt - pendingTask.enqueuedAt : now - pendingTask.enqueuedAt,
      executionTime: pendingTask.executedAt ? now - pendingTask.executedAt : 0
    }));
  }
  async execute(pendingTask) {
    logger.debug("execute() [name:".concat(pendingTask.name, "]"));
    if (pendingTask.executedAt) {
      throw new Error('task already being executed');
    }
    pendingTask.executedAt = Date.now();
    try {
      const result = await pendingTask.task();
      // Resolve the task with its resolved result (if any).
      pendingTask.resolve(result);
    } catch (error) {
      // Reject the task with its rejected error.
      pendingTask.reject(error);
    }
  }
}
exports.AwaitQueue = AwaitQueue;